import React, { useRef, useState, useEffect } from "react";
import {
  FaVolumeMute,
  FaVolumeUp,
  FaExpand,
  FaCompress,
  FaUser,
} from "react-icons/fa";
import Hls from "hls.js";
import { useNavigate, useParams } from "react-router";
import videojs from "video.js";
import performRequest from "../../../api/request";
import { CustomErrorResponse } from "../../../CustomComponent/custom_error_response";
import { socket } from "../../../api/socket.io";
import "./aws_player.css";
import WebinarChat from "./webinarChat";
import WebinarPoll from "./webinarPoll";
import { DateTime } from "luxon";

const getDayNumberInIST = (datetimeStr) => {
  const date = DateTime.fromISO(datetimeStr, { zone: "Asia/Kolkata" });
  return date.isValid ? date.weekday % 7 : null; // Luxon: 1 (Mon) to 7 (Sun)
};

const Showlivewebinar = ({ userData }) => {
  const { id } = useParams();
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(50);
  const [error, setError] = useState(null);
  const [isEnd, setIsEnd] = useState(false);
  const [networkError, setNetworkError] = useState(false);
  const [pollNumber, setPollNumber] = useState(null);
  const [videoURL, setVideoURL] = useState("");
  const [lastSegmentTime, setLastSegmentTime] = useState(Date.now());
  const [peopleCounter, setPeopleCounter] = useState(0);
  const [joinedUsers, setJoinedUsers] = useState([]);
  const [showControls, setShowControls] = useState(true);
  const [videoAspectRatio, setVideoAspectRatio] = useState(16 / 9);
  const [blackBarHeight, setBlackBarHeight] = useState({
    top: 0,
    bottom: 0,
    total: 0,
  });
  const segmentTimeout = useRef(null);
  const hideControlsTimeout = useRef(null);
  const navigate = useNavigate();
  const SEGMENT_TIMEOUT_THRESHOLD = 9000;

  // Detect video aspect ratio
  useEffect(() => {
    const videoElement = videoRef.current;
    if (videoElement) {
      const handleLoadedMetadata = () => {
        const { videoWidth, videoHeight } = videoElement;
        if (videoWidth && videoHeight) {
          setVideoAspectRatio(videoWidth / videoHeight);
        }
      };
      videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);
      return () =>
        videoElement.removeEventListener(
          "loadedmetadata",
          handleLoadedMetadata
        );
    }
  }, [videoURL]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (videoElement) {
      const calculateBlackBarHeight = () => {
        const { videoWidth, videoHeight, clientWidth, clientHeight } =
          videoElement;
        if (videoWidth && videoHeight && clientWidth && clientHeight) {
          const videoAspect = videoWidth / videoHeight;
          const containerAspect = clientWidth / clientHeight;

          let scaledHeight;
          if (videoAspect > containerAspect) {
            scaledHeight = clientWidth / videoAspect;
          } else {
            scaledHeight = clientHeight;
          }
          const totalBlackBarHeight = clientHeight - scaledHeight;
          const singleBlackBarHeight =
            totalBlackBarHeight > 0 ? totalBlackBarHeight / 2 : 0;
          setBlackBarHeight({
            top: singleBlackBarHeight,
            bottom: singleBlackBarHeight,
            total: totalBlackBarHeight > 0 ? totalBlackBarHeight : 0,
          });
        }
      };

      videoElement.addEventListener("loadedmetadata", calculateBlackBarHeight);
      videoElement.addEventListener("resize", calculateBlackBarHeight);
      window.addEventListener("resize", calculateBlackBarHeight);

      return () => {
        videoElement.removeEventListener(
          "loadedmetadata",
          calculateBlackBarHeight
        );
        videoElement.removeEventListener("resize", calculateBlackBarHeight);
        window.removeEventListener("resize", calculateBlackBarHeight);
      };
    }
  }, [videoURL]);

  // Video and webinar logic
  const showLoader = () => setIsLoading(true);
  const hideLoader = () => setIsLoading(false);
  const showError = (message) => setError(message);

  const fetchRealTime = () => {
    performRequest("GET", "/attendance/getCurrentTime/")
      .then((res) => {
        if (res.data?.datetime) {
          const days = [
            "Sunday",
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
          ];
          const day = days[getDayNumberInIST(res.data?.datetime)];

          if (day === "Saturday") {
            setVideoURL(
              "https://dob9vcuw5jx4n.cloudfront.net/saturday-webinar-op/index.m3u8"
            );
          } else if (day === "Sunday") {
            setVideoURL(
              "https://dob9vcuw5jx4n.cloudfront.net/sunday-webinar-op/index.m3u8"
            );
          } else {
            setVideoURL(
              "https://dob9vcuw5jx4n.cloudfront.net/friday-webinar-op/index.m3u8"
            );
          }
          setNetworkError(false);
        }
      })
      .catch((err) => CustomErrorResponse(err));
  };

  const getNetwork = () => {
    performRequest("GET", "/attendance/getCurrentTime/")
      .then((res) => {
        if (res.data?.datetime) {
          setNetworkError(false);
        }
      })
      .catch((err) => console.log(err));
  };

  const monitorSegmentTimeout = () => {
    segmentTimeout.current = setTimeout(() => {
      if (Date.now() - lastSegmentTime > SEGMENT_TIMEOUT_THRESHOLD) {
        showLoader();
      }
    }, SEGMENT_TIMEOUT_THRESHOLD);
  };

  useEffect(() => {
    fetchRealTime();
  }, []);

  const handleNewUserJoin = (username, city) => {
    const id = Date.now();
    setJoinedUsers((prev) => [...prev, { id, username, city }]);
    setTimeout(() => {
      setJoinedUsers((prev) => prev.filter((user) => user.id !== id));
    }, 5000);
  };

  useEffect(() => {
    if (userData && userData?.name) {
      handleNewUserJoin(userData?.name, userData?.city || "");
    }

    socket.on("emmitWebUser", (emmitWebUser) => {
      if (emmitWebUser && emmitWebUser?.randomUser && emmitWebUser.randomCity) {
        handleNewUserJoin(emmitWebUser.randomUser, emmitWebUser.randomCity);
      }
      if (emmitWebUser && emmitWebUser?.userCount) {
        setPeopleCounter(emmitWebUser.userCount);
      }
    });

    return () => {
      socket.off("emmitWebUser");
    };
  }, [userData, socket]);

  useEffect(() => {
    if (!error && videoURL) {
      const videoElement = videoRef.current;
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

      if (isIOS && videoElement.canPlayType("application/vnd.apple.mpegurl")) {
        videoElement.src = videoURL;
        const handleCanPlay = () => {
          videoElement.removeEventListener("canplay", handleCanPlay);
          videoElement
            .play()
            .catch((err) => console.error("Error playing video:", err));
        };
        videoElement.addEventListener("canplay", handleCanPlay);
        return () => videoElement.removeEventListener("canplay", handleCanPlay);
      } else if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          backBufferLength: 3,
          liveSyncDurationCount: 3, // Added: Sync to the latest 3 segments
          liveMaxLatencyDurationCount: 4, // Added: Allow slight latency but cap at 6 segments
          maxBufferLength: 3,
          manifestLoadingTimeOut: 1000, // Frequent playlist reload
          manifestLoadingMaxRetry: 10,
        });
        hls.loadSource(videoURL);
        hls.attachMedia(videoElement);
        const days = [
          "Sunday",
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ];
        const day = days[DateTime.now().setZone("Asia/Kolkata").weekday % 7];
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (videoElement.readyState >= 2) {
            videoElement
              .play()
              .catch((err) => console.error("Error playing video:", err));
          } else {
            const handleCanPlay = () => {
              videoElement.removeEventListener("canplay", handleCanPlay);
              videoElement
                .play()
                .catch((err) => console.error("Error playing video:", err));
            };
            videoElement.addEventListener("canplay", handleCanPlay);
          }
        });

        hls.on(Hls.Events.FRAG_LOADED, (event, data) => {
          setLastSegmentTime(Date.now());
          hideLoader();
          clearTimeout(segmentTimeout.current);
          monitorSegmentTimeout();
        });
        hls.on(Hls.Events.FRAG_CHANGED, (event, data) => {
          const latestFragment = data.frag;
          const baseSegmetUrl = latestFragment._url;
          let segmentUrl = baseSegmetUrl?.split("?")[0];
          const segmentNumber = segmentUrl?.split("/").pop();

          if (day === "Saturday") {
            if (segmentNumber.includes("00394")) {
              setPollNumber(0);
            } else if (segmentNumber.includes("00503")) {
              setPollNumber(1);
            } else if (segmentNumber.includes("00678")) {
              setPollNumber(2);
            }
          } else {
            if (segmentNumber.includes("00436")) {
              setPollNumber(0);
            } else if (segmentNumber.includes("00620")) {
              setPollNumber(1);
            } else if (segmentNumber.includes("00752")) {
              setPollNumber(2);
            }
          }

          if (latestFragment.endList) {
            alert("Webinar has ended, Hit OK to Add Feedback");
            navigate(`/add-webinar-feedback/${id}`);
          }
        });

        hls.on(Hls.Events.LEVEL_LOADED, (event, data) => {
          showError(null);
          setError(null);
          if (!data.details.live) {
            segmentTimeout.current = setTimeout(() => {
              if (Date.now() - lastSegmentTime > SEGMENT_TIMEOUT_THRESHOLD) {
                setIsLoading(false);
                setIsEnd(true);
                showError("The stream has ended.");
              }
            }, SEGMENT_TIMEOUT_THRESHOLD);
          }
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                showError("Network issue. Trying to recover...");
                setNetworkError(true);
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                showError("Playback issue. Attempting recovery...");
                hls.recoverMediaError();
                break;
              default:
                showError("Something went wrong with the stream.");
                hls.destroy();
                break;
            }
          }
        });
        return () => {
          hls.destroy();
        };
      }

      videojs(videoElement);
    }
  }, [videoURL, error, networkError]);

  const toggleFullscreen = () => {
    const videoContainer = videoContainerRef.current;
    const videoElement = videoRef.current;
    if (!videoContainer || !videoElement) return;

    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const requestFullscreen =
      videoContainer.requestFullscreen ||
      videoContainer.webkitRequestFullscreen ||
      videoContainer.mozRequestFullScreen ||
      videoContainer.msRequestFullscreen;
    const exitFullscreen =
      document.exitFullscreen ||
      document.webkitExitFullscreen ||
      document.mozCancelFullScreen ||
      document.msExitFullscreen;
    const fullscreenElement =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;

    if (!fullscreenElement && !isMaximized) {
      if (isIOS) {
        videoContainer.style.width = "100vw";
        videoContainer.style.height = "100vh";
        videoContainer.style.position = "fixed";
        videoContainer.style.top = "0";
        videoContainer.style.left = "0";
        videoContainer.style.zIndex = "9999";
        videoContainer.style.background = "black";
        videoContainer.classList.add("maximized");
        setIsMaximized(true);
        setIsFullscreen(true);
      } else if (requestFullscreen) {
        requestFullscreen
          .call(videoContainer)
          .then(() => {
            setIsFullscreen(true);
          })
          .catch((error) => {
            console.error("Failed to enter fullscreen:", error);
          });
      } else {
        videoContainer.style.width = "100vw";
        videoContainer.style.height = "100vh";
        videoContainer.style.position = "fixed";
        videoContainer.style.top = "0";
        videoContainer.style.left = "0";
        videoContainer.style.zIndex = "9999";
        videoContainer.style.background = "black";
        videoContainer.classList.add("maximized");
        setIsMaximized(true);
        setIsFullscreen(true);
      }
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
        if ("orientation" in window) {
          window.dispatchEvent(new Event("orientationchange"));
        }
      }, 300);
    } else {
      if (fullscreenElement && exitFullscreen) {
        exitFullscreen
          .call(document)
          .then(() => {
            setIsFullscreen(false);
            videoContainer.classList.remove("maximized");
          })
          .catch((error) => {
            console.error("Failed to exit fullscreen:", error);
          });
      } else if (isMaximized) {
        videoContainer.style.width = "";
        videoContainer.style.height = "";
        videoContainer.style.position = "";
        videoContainer.style.top = "";
        videoContainer.style.left = "";
        videoContainer.style.zIndex = "";
        videoContainer.style.background = "";
        videoContainer.classList.remove("maximized");
        setIsMaximized(false);
        setIsFullscreen(false);
        setTimeout(() => {
          window.dispatchEvent(new Event("resize"));
        }, 300);
      }
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseInt(e.target.value, 10);
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    if (videoRef.current) {
      videoRef.current.volume = newVolume / 100;
      videoRef.current.muted = newVolume === 0;
    }
  };

  const handleUserActivity = () => {
    setShowControls(true);
    if (hideControlsTimeout.current) {
      clearTimeout(hideControlsTimeout.current);
    }
    hideControlsTimeout.current = setTimeout(() => {
      setShowControls(false);
    }, 5000);
  };

  useEffect(() => {
    document.addEventListener("click", handleUserActivity);
    document.addEventListener("touchstart", handleUserActivity);
    return () => {
      document.removeEventListener("click", handleUserActivity);
      document.removeEventListener("touchstart", handleUserActivity);
      if (hideControlsTimeout.current) {
        clearTimeout(hideControlsTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
      videoRef.current.volume = volume / 100;
    }
  }, [isMuted, volume]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isInFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isInFullscreen || isMaximized);
      if (videoContainerRef.current && !isInFullscreen && !isMaximized) {
        videoContainerRef.current.classList.remove("maximized");
      }
    };

    const handleWebkitEndFullscreen = () => {
      setIsFullscreen(false);
      setIsMaximized(false);
      if (videoContainerRef.current) {
        videoContainerRef.current.classList.remove("maximized");
        videoContainerRef.current.style.width = "";
        videoContainerRef.current.style.height = "";
        videoContainerRef.current.style.position = "";
        videoContainerRef.current.style.top = "";
        videoContainerRef.current.style.left = "";
        videoContainerRef.current.style.zIndex = "";
        videoContainerRef.current.style.background = "";
      }
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 300);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);
    if (videoRef.current) {
      videoRef.current.addEventListener(
        "webkitendfullscreen",
        handleWebkitEndFullscreen
      );
    }

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange
      );
      if (videoRef.current) {
        videoRef.current.removeEventListener(
          "webkitendfullscreen",
          handleWebkitEndFullscreen
        );
      }
    };
  }, [isMaximized]);
  // useEffect(() => {
  //   const watermark = document.getElementById("watermark-div");
  //   if (userData && watermark) {
  //     watermark.textContent = `User: ${userData.email}  |  ${
  //       userData.mobile
  //     } | ${new Date().toLocaleString()}`;
  //   }
  // }, [userData]);

  useEffect(() => {
    let intervalId;
    if (networkError) {
      intervalId = setInterval(() => {
        getNetwork();
      }, 5000);
    } else {
      showError(null);
      setNetworkError(false);
    }
    return () => {
      if (networkError === false) {
        if (intervalId) {
          clearInterval(intervalId);
        }
      }
    };
  }, [networkError]);

  return (
    <div className="container-fluid  ">
      <div
        className="position-relative video-container "
        style={{ aspectRatio: videoAspectRatio }}
        ref={videoContainerRef}
      >
        {!error && (
          <div
            className="position-absolute m-1 d-flex align-items-center bg-transparent text-white  py-1 gap-1 z-100 live-container"
            style={{ top: blackBarHeight?.top }}
          >
            <span className="status status-red">
              <span
                className="status-dot status-dot-animated me-0"
                style={{ width: "8px", height: "8px" }}
              ></span>
              <span className="live-text">LIVE</span>
              {peopleCounter ? (
                <div className="d-flex align-items-center">
                  <FaUser className="me-1 live-icon" size={10} />
                  <span className="people-count">{peopleCounter}</span>
                </div>
              ) : null}
            </span>
          </div>
        )}

        {!error && (
          <video
            ref={videoRef}
            className="rounded-0 w-100"
            controls={false}
            autoPlay
            muted={isMuted}
            playsInline
            webkit-playsinline="true"
          />
        )}

        {!error && (
          <div
            className="position-absolute   start-0 ms-1 ms-md-3 mb-1 d-flex flex-column-reverse gap-1 joined-users-mobile-limit"
            style={
              isFullscreen
                ? { bottom: blackBarHeight?.bottom }
                : { bottom: "0" }
            }
          >
            {joinedUsers.map((user) => (
              <div key={user.id}>
                <p className="text-left joined-user px-2 px-md-3 py-1">
                  {user.username} from {user.city} joined
                </p>
              </div>
            ))}
          </div>
        )}

        {isLoading && (
          <div className="position-absolute top-50 start-50 translate-middle z-100">
            <div className="spinner-overlay">
              <div className="spinner-border spinner-border-lg text-dark"></div>
            </div>
          </div>
        )}

        {/*  modal   */}

        {(pollNumber === 0 || pollNumber === 1 || pollNumber === 2) && (
          <WebinarPoll pollNumber={pollNumber} setPollNumber={setPollNumber} />
        )}

        {showControls && (
          <div
            className="position-absolute start-50 translate-middle-x mb-2 bg-dark px-3 py-2 rounded shadow d-flex align-items-center gap-3 z-1000 controls-container"
            style={
              isFullscreen
                ? { bottom: blackBarHeight?.bottom }
                : { bottom: "0" }
            }
          >
            <FaVolumeMute
              onClick={toggleMute}
              hidden={!isMuted}
              className="text-white cursor-pointer"
              aria-label="Unmute"
            />
            <FaVolumeUp
              onClick={toggleMute}
              hidden={isMuted}
              className="text-white cursor-pointer"
              aria-label="Mute"
            />
            <input
              type="range"
              min="0"
              max="100"
              value={volume}
              onChange={handleVolumeChange}
              className="form-range w-80"
              style={{ maxWidth: "100px" }}
              aria-label="Volume"
            />
            <FaExpand
              onClick={toggleFullscreen}
              hidden={isFullscreen}
              className="text-white cursor-pointer"
              aria-label="Enter fullscreen"
            />
            <FaCompress
              onClick={toggleFullscreen}
              hidden={!isFullscreen}
              className="text-white cursor-pointer"
              aria-label="Exit fullscreen"
            />
          </div>
        )}
        {(error || isEnd) && (
          <div className="position-absolute top-50 start-50 translate-middle text-center z-100 text-white">
            {error && <div style={{ color: "red" }}>{error}</div>}
            {isEnd && <div>The stream has ended.</div>}
          </div>
        )}
        {/* <div
          className="live-text position-absolute z-100"
          style={{ top: blackBarHeight?.top }}
          id="watermark-div"
        ></div> */}
      </div>
      <WebinarChat />
    </div>
  );
};

export default Showlivewebinar;
